/**
 * @file ContentArticlesListPage.tsx
 * @description Список статей блога с фильтрами (phase12.0).
 */

import { Plus, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router";

import { Alert } from "../../../components/ui/alert";
import { Button } from "../../../components/ui/button";
import { EditorialCard } from "../../../components/ui/EditorialCard";
import { Input } from "../../../components/ui/input";
import { ApiError } from "../../../lib/api-client";
import {
  contentArticles,
  type ArticleListItem,
  type ContentArticleStatus,
} from "../../../lib/api-content";

const STATUS_LABELS: Record<ContentArticleStatus, { label: string; className: string }> = {
  DRAFT: { label: "Черновик", className: "bg-amber-100 text-amber-800" },
  PUBLISHED: { label: "Опубликовано", className: "bg-emerald-100 text-emerald-800" },
  ARCHIVED: { label: "Архив", className: "bg-on-surface/10 text-on-surface-muted" },
};

const DATE_FMT = new Intl.DateTimeFormat("ru-RU", {
  day: "numeric",
  month: "long",
  year: "numeric",
});

export function ContentArticlesListPage() {
  const [articles, setArticles] = useState<ArticleListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<ContentArticleStatus | "ALL">("ALL");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Простой дебаунс — без зависимостей.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const data = await contentArticles.list({
          status: statusFilter === "ALL" ? undefined : statusFilter,
          search: debouncedSearch.trim() || undefined,
        });
        setArticles(data);
        setError(null);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : "Не удалось загрузить");
      } finally {
        setLoading(false);
      }
    }
    void load();
  }, [statusFilter, debouncedSearch]);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="label-editorial">Контент · Блог</div>
          <h1 className="text-3xl font-semibold tracking-tight mt-1">Статьи</h1>
        </div>
        <Button asChild>
          <Link to="new" className="inline-flex items-center gap-2">
            <Plus className="h-4 w-4" /> Новая статья
          </Link>
        </Button>
      </div>

      {error ? <Alert severity="error">{error}</Alert> : null}

      <EditorialCard>
        <EditorialCard.Body>
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-on-surface-muted" />
              <Input
                placeholder="Поиск по заголовку…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="flex gap-1">
              {(["ALL", "DRAFT", "PUBLISHED", "ARCHIVED"] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStatusFilter(s)}
                  className={`rounded-full px-3 py-1 text-sm transition-colors ${
                    statusFilter === s
                      ? "bg-primary text-on-primary"
                      : "bg-on-surface/8 text-on-surface-muted hover:bg-on-surface/15"
                  }`}
                >
                  {s === "ALL" ? "Все" : STATUS_LABELS[s].label}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <p className="text-sm text-on-surface-muted">Загрузка…</p>
          ) : articles.length === 0 ? (
            <p className="text-sm text-on-surface-muted">Статей нет.</p>
          ) : (
            <ul className="flex flex-col divide-y divide-on-surface/10">
              {articles.map((a) => (
                <li key={a.id}>
                  <Link
                    to={a.id}
                    className="flex items-start gap-4 py-4 transition-colors hover:bg-on-surface/5"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_LABELS[a.status].className}`}
                        >
                          {STATUS_LABELS[a.status].label}
                        </span>
                        <span className="text-xs text-on-surface-muted">
                          {a.categoryName}
                        </span>
                        {a.publishedAt && (
                          <span className="text-xs text-on-surface-muted">
                            · {DATE_FMT.format(new Date(a.publishedAt))}
                          </span>
                        )}
                      </div>
                      <div className="mt-1 font-semibold text-on-surface line-clamp-2">
                        {a.title}
                      </div>
                      <div className="text-sm text-on-surface-muted line-clamp-2 mt-1">
                        {a.excerpt}
                      </div>
                      <div className="mt-1 text-xs text-on-surface-muted">
                        {a.authorName}
                        {a.readingTimeMin && ` · ${a.readingTimeMin} мин чтения`}
                      </div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </EditorialCard.Body>
      </EditorialCard>
    </div>
  );
}
